// React imports
import { useState, useEffect } from 'react';

// General imports
import axios from 'axios';
import _ from 'lodash';

// MUI imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { green } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import SendEmailUsingExternalServiceDialog from './SendEmailUsingExternalServiceDialog.js';
import readableId from '../../ReadableId.js';
import email from '../utils/email';
import { allFieldsAssignedToSections } from '../opportunities/freetext/OpportunityOneOffFreeTextBuilder.utils.js';
import { getDefaultEmailBody, getEmailSubject } from '../opportunities/OpportunityOneOffEmailBuilder.utils.js';

function SendOpportunityDialog(props) {
    const { onSent, onClose, emailLoginInfo, bodyFooter, opportunity, accesstoken,
        initialBody, initialSubject, toRecipients, org, minimized, attachPDF, setAttachPDF, logFollowUp = false } = props;
    const [ isSending, setIsSending ] = useState(false);
    const [ isDrafting, setIsDrafting ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ sent, setSent ] = useState(false);
    const [ link, setLink ] = useState(null);
    const [ queuedEmailId, setQueuedEmailId ] = useState(null);
    const [ isPolling, setIsPolling ] = useState(false);
    const [ pendingRecipients, setPendingRecipients ] = useState({ toRecipients: [], ccRecipients: [] });

    useEffect(() => {
        (async () => {
            const { data } = await axios.get(`/api/opportunities/${opportunity.id}/offer-url?accesstoken=${accesstoken}&type=end-customer&organizationId=${org.id}&minimized=${minimized ? 'true' : 'false'}`);
            setLink(data);
        })();
    }, [ toRecipients, minimized ]);

    async function apiSend({ apiFunc, body, subject, toRecipients, ccRecipients }) {
        setError(null);
        try {
            const _body = body + (bodyFooter ? `<br/><br/><div style="font-size: 9px; color: #888;">${bodyFooter}</div>` : '')
            let result = null;
            if (apiFunc === 'sendMail') {
                result = await email.sendEmail({
                    org,
                    tokens: emailLoginInfo.tokens,
                    toRecipients,
                    ccRecipients,
                    subject,
                    body: _body,
                    attachments: [],
                    monadaAccesstoken: accesstoken,
                    monadaOrganizationId: org.id,
                    useQueue: true,
                    generatePdf: attachPDF ? { opportunityId: opportunity.id, minimized: minimized === true } : null,
                    opportunityId: opportunity.id,
                    logFollowUp,
                });

                // Start polling for email status
                setQueuedEmailId(result.id);
                setPendingRecipients({ toRecipients, ccRecipients });
                setIsPolling(true);
                const pollResult = await email.waitForEmailSent({
                    org,
                    emailId: result.id,
                    monadaAccesstoken: accesstoken
                });

                setIsPolling(false);

                if (pollResult.success) {
                    setSent([{ conversationId: pollResult.email.conversationId, internetMessageId: pollResult.email.internetMessageId, toRecipients: toRecipients, ccRecipients: ccRecipients }]);
                    onSent([{ conversationId: pollResult.email.conversationId, internetMessageId: pollResult.email.internetMessageId, toRecipients: toRecipients, ccRecipients: ccRecipients }]);
                } else {
                    setError(pollResult.error || 'Failed to send email');
                    setIsSending(false);
                }
            } else {
                result = await email.createDraft({
                    org,
                    tokens: emailLoginInfo.tokens,
                    toRecipients,
                    ccRecipients,
                    subject,
                    body: _body,
                    attachments: [],
                    monadaAccesstoken: accesstoken,
                    monadaOrganizationId: org.id
                });

                setSent([{ conversationId: result.conversationId, internetMessageId: result.internetMessageId, toRecipients: toRecipients, ccRecipients: ccRecipients }]);
                onSent([{ conversationId: result.conversationId, internetMessageId: result.internetMessageId, toRecipients: toRecipients, ccRecipients: ccRecipients }]);
            }
        } catch (e) {
            console.error(e);
            setError(e.message);
            setIsSending(false);
            setIsDrafting(false);
            setIsPolling(false);
        }
    }

    const handleContinueInBackground = () => {
        // Call onSent with the queued email info so the parent knows it was queued
        onSent([{ queued: true, emailId: queuedEmailId, toRecipients: pendingRecipients.toRecipients, ccRecipients: pendingRecipients.ccRecipients }]);
        onClose();
    };

    const onSend = async ({ body, subject, toRecipients, ccRecipients }) => {
        setIsSending(true);
        await apiSend({ apiFunc: 'sendMail', body, subject, toRecipients, ccRecipients });
    };

    const onDraft = async ({ body, subject, toRecipients, ccRecipients }) => {
        setIsDrafting(true);
        await apiSend({ apiFunc: 'messages', body, subject, toRecipients, ccRecipients });
    };

    if (!link) {
        return (
            <Box sx={{ maxWidth: '75vw', minWidth: '75vw', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                <CircularProgress />
            </Box>
        )
    }

    if (isPolling) return (
        <Box sx={{ maxWidth: '75vw', minWidth: '75vw', p: 3 }} >
            <Box sx={{ padding: '24px 0px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={64} />
            </Box>
            <Box sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Your email is being sent...
            </Box>
            <Box sx={{ display: 'flex', marginTop: '24px', flexDirection: 'row-reverse' }}>
                <Button
                    color='primary'
                    variant='outlined'
                    onClick={handleContinueInBackground}
                >
                    Continue in background...
                </Button>
            </Box>
        </Box>
    )

    if (sent) return (
        <Box sx={{ maxWidth: '75vw', minWidth: '75vw', p: 3 }} >
            <Box sx={{ padding: '24px 0px', color: green[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faCircleCheck} beat size='4x' style={{ animationIterationCount: 1 }} />
            </Box>
            <Box sx={{ fontWeight: 800, color: green[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isDrafting ? 'Draft created successfully!' : 'Email sent successfully!'}
            </Box>
            <Box sx={{ display: 'flex', marginTop: '24px', flexDirection: 'row-reverse' }}>
                <Button
                    color='success'
                    variant='outlined'
                    onClick={() => onClose()}
                >
                    Close
                </Button>
            </Box>
        </Box>
    )

    return (
        <SendEmailUsingExternalServiceDialog
            {...props}
            initialBody={initialBody ? initialBody.replace(/temporaryAccesstoken=[^"]*/g, `temporaryAccesstoken=${link}`) :
                getDefaultEmailBody({ 
                    organization: org, 
                    transportationMethod: opportunity.transportationMethod, 
                    link: `https://app.monada.ai/offers?temporaryAccesstoken=${link}` 
                })
            }
            initialSubject={
                initialSubject ||
                getEmailSubject({ organization: org, opportunity, readableId: readableId(opportunity) })
            }
            receipientEditable={'multiple'}
            selectedAccountId={opportunity.account?.id}
            buttons={[{
                variant: 'contained',
                color: 'primary',
                func: onSend,
                sx: { width: '200px', minWidth: '200px' },
                disabled: isSending || isDrafting,
                children: isSending ? <CircularProgress size={16} /> :
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        &nbsp;
                        Send
                    </Box>

            }]}
            editorFooter={(
                <Box>
                    {opportunity.type === 'one-off-free-text' && (
                        <Typography variant='caption' component={Box} sx={{ mb: 2 }}>
                            <b>Note:</b> Changes made in this window will only effect the email that is sent. If you want to make global changes, please use the offer editor.
                        </Typography>
                    )}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={attachPDF}
                                onChange={e => setAttachPDF(e.target.checked)}
                                color='primary'
                                size='small'
                            />
                        }
                        label={
                            <Box>
                                <Typography variant='body2'>Add the offer as a PDF attachment.</Typography>
                                {attachPDF && opportunity?.attributes?.viewTemplate && (
                                    <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', marginTop: '4px' }}>
                                        Template: {opportunity.attributes.viewTemplate === 'contract' ? 'Contract' : 'Standard'}
                                    </Typography>
                                )}
                            </Box>
                        }
                    />
                    {attachPDF && opportunity.type === 'one-off-free-text' && !allFieldsAssignedToSections({ offers: opportunity.offers }) && (
                        <Alert severity='error'>
                            <Typography variant='body2'>
                                Not all fields are assigned to a section. <b>PDF sent may contain wrong information.</b>
                            </Typography>
                        </Alert>
                    )}
                    {error ? <Alert severity='error'>{error}</Alert> : null}
                    {isSending && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}>
                            <Typography variant='caption' sx={{ width: '210px'}}>
                                Please don't close this window or refresh the page while we're sending the email.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        />
    )
}

export default SendOpportunityDialog;